/**
 * Конструктор: объекты, поля, статусы, переходы, формулы, справочники, шаблоны.
 *
 * Всё это — данные, а не код: экраны ниже правят строки в базе, и после их
 * правки система ведёт себя иначе без участия разработчика.
 */

import { newIdempotencyKey, request } from "@/shared/api/client";

// ── Объекты ──────────────────────────────────────────────────────────────────

export interface TaskObject {
  id: number;
  code: string;
  name: string;
  icon: string | null;
  entity: string;
  is_system: boolean;
  is_active: boolean;
  position: number;
  card: Record<string, unknown>;
}

export function listObjects(): Promise<TaskObject[]> {
  return request<TaskObject[]>("/api/objects");
}

export function defaultObject(): Promise<TaskObject> {
  return request<TaskObject>("/api/objects/default");
}

export function createObject(draft: {
  code: string;
  name: string;
  icon?: string | null;
}): Promise<TaskObject> {
  return request<TaskObject>("/api/objects", {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function updateObject(
  objectId: number,
  patch: Partial<Pick<TaskObject, "name" | "icon" | "is_active" | "position" | "card">>,
): Promise<TaskObject> {
  return request<TaskObject>(`/api/objects/${objectId}`, { method: "PATCH", body: patch });
}

export function deleteObject(objectId: number): Promise<undefined> {
  return request<undefined>(`/api/objects/${objectId}`, { method: "DELETE" });
}

// ── Наборы полей ─────────────────────────────────────────────────────────────

export interface FieldGroup {
  id: number;
  object_id: number;
  name: string;
  tab: string | null;
  position: number;
  columns: number;
  is_collapsed: boolean;
}

export function listFieldGroups(objectId: number): Promise<FieldGroup[]> {
  return request<FieldGroup[]>(`/api/objects/${objectId}/field-groups`);
}

export function createFieldGroup(
  objectId: number,
  draft: { name: string; tab?: string | null; position?: number; columns?: number },
): Promise<FieldGroup> {
  return request<FieldGroup>(`/api/objects/${objectId}/field-groups`, {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function updateFieldGroup(
  groupId: number,
  patch: Partial<Omit<FieldGroup, "id" | "object_id">>,
): Promise<FieldGroup> {
  return request<FieldGroup>(`/api/field-groups/${groupId}`, { method: "PATCH", body: patch });
}

export function deleteFieldGroup(groupId: number): Promise<undefined> {
  return request<undefined>(`/api/field-groups/${groupId}`, { method: "DELETE" });
}

// ── Поля ─────────────────────────────────────────────────────────────────────

export interface Field {
  id: number;
  object_id: number | null;
  group_id: number | null;
  entity: string;
  directory_id: number | null;
  code: string;
  name: string;
  type: string;
  config: Record<string, unknown>;
  hint: string | null;
  default_value: Record<string, unknown>;
  visibility: Record<string, unknown>;
  editable_by: Record<string, unknown>;
  formula: string | null;
  is_indexed: boolean;
  is_multi: boolean;
  is_required: boolean;
  is_system: boolean;
  position: number;
}

export interface FieldTypeInfo {
  key: string;
  title: string;
  group: string;
  is_reference: boolean;
  is_derived: boolean;
  supports_multi: boolean;
  config_schema: Record<string, string>;
}

export const FIELD_GROUP_TITLES: Record<string, string> = {
  scalar: "Простые",
  enum: "Перечисления",
  media: "Файлы и гео",
  reference: "Ссылочные",
  derived: "Вычисляемые",
};

export function listFieldTypes(): Promise<FieldTypeInfo[]> {
  return request<FieldTypeInfo[]>("/api/field-types");
}

export function listFields(params: {
  object_id?: number;
  directory_id?: number;
  entity?: string;
}): Promise<Field[]> {
  const query = new URLSearchParams();
  if (params.object_id !== undefined) query.set("object_id", String(params.object_id));
  if (params.directory_id !== undefined) query.set("directory_id", String(params.directory_id));
  if (params.entity) query.set("entity", params.entity);
  return request<Field[]>(`/api/fields?${query.toString()}`);
}

export interface FieldDraft {
  code: string;
  name: string;
  type: string;
  object_id?: number | null;
  directory_id?: number | null;
  entity?: string;
  group_id?: number | null;
  config?: Record<string, unknown>;
  hint?: string | null;
  formula?: string | null;
  is_indexed?: boolean;
  is_multi?: boolean;
  is_required?: boolean;
  position?: number;
}

export function createField(draft: FieldDraft): Promise<Field> {
  return request<Field>("/api/fields", {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function updateField(
  fieldId: number,
  patch: Partial<Omit<FieldDraft, "code" | "type">>,
): Promise<Field> {
  return request<Field>(`/api/fields/${fieldId}`, { method: "PATCH", body: patch });
}

export function deleteField(fieldId: number): Promise<undefined> {
  return request<undefined>(`/api/fields/${fieldId}`, { method: "DELETE" });
}

// ── Индексы полей ────────────────────────────────────────────────────────────

export interface FieldIndexState {
  field_id: number;
  index_name: string;
  state: string;
  title: string;
  error: string | null;
}

export function listFieldIndexes(): Promise<FieldIndexState[]> {
  return request<FieldIndexState[]>("/api/field-indexes");
}

// ── Статусы и переходы ───────────────────────────────────────────────────────

export interface Status {
  id: number;
  code: string;
  name: string;
  kind: string;
  color: string;
  position: number;
  is_final: boolean;
  is_initial: boolean;
  requires_comment: boolean;
}

export const STATUS_KINDS: { value: string; label: string }[] = [
  { value: "new", label: "Новая" },
  { value: "in_progress", label: "В работе" },
  { value: "done", label: "Завершена" },
  { value: "rejected", label: "Отменена" },
];

export const STATUS_COLORS: { value: string; label: string }[] = [
  { value: "neutral", label: "Серый" },
  { value: "info", label: "Синий" },
  { value: "warning", label: "Янтарный" },
  { value: "success", label: "Зелёный" },
  { value: "danger", label: "Красный" },
];

export function listStatuses(objectId?: number): Promise<Status[]> {
  const suffix = objectId === undefined ? "" : `?object_id=${objectId}`;
  return request<Status[]>(`/api/statuses${suffix}`);
}

export function createStatus(
  objectId: number,
  draft: {
    code: string;
    name: string;
    kind: string;
    color?: string;
    position?: number;
    is_initial?: boolean;
    requires_comment?: boolean;
  },
): Promise<Status> {
  return request<Status>(`/api/statuses?object_id=${objectId}`, {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function updateStatus(
  statusId: number,
  patch: {
    name?: string;
    kind?: string;
    color?: string;
    position?: number;
    is_initial?: boolean;
    requires_comment?: boolean;
  },
): Promise<Status> {
  return request<Status>(`/api/statuses/${statusId}`, { method: "PATCH", body: patch });
}

export function deleteStatus(statusId: number): Promise<undefined> {
  return request<undefined>(`/api/statuses/${statusId}`, { method: "DELETE" });
}

export interface Transition {
  id: number;
  object_id: number;
  from_status_id: number | null;
  to_status_id: number;
  roles: string[];
  condition: Record<string, unknown>;
  requires_comment: boolean;
}

export interface TransitionDraft {
  from_status_id: number | null;
  to_status_id: number;
  roles?: string[];
  requires_comment?: boolean;
}

export function listTransitions(objectId: number): Promise<Transition[]> {
  return request<Transition[]>(`/api/objects/${objectId}/transitions`);
}

export function saveTransitions(
  objectId: number,
  transitions: TransitionDraft[],
): Promise<Transition[]> {
  return request<Transition[]>(`/api/objects/${objectId}/transitions`, {
    method: "PUT",
    body: { transitions },
  });
}

// ── Формулы ──────────────────────────────────────────────────────────────────

export interface FormulaFunction {
  name: string;
  group: string;
  summary: string;
}

export interface FormulaCheck {
  ok: boolean;
  value: string | null;
  error: string | null;
  position: number | null;
  hint: string | null;
}

export function listFormulaFunctions(): Promise<FormulaFunction[]> {
  return request<FormulaFunction[]>("/api/formula/functions");
}

export function checkFormula(draft: {
  formula: string;
  object_id?: number | null;
  values?: Record<string, unknown>;
}): Promise<FormulaCheck> {
  return request<FormulaCheck>("/api/formula/check", { method: "POST", body: draft });
}

export function recalculate(objectId: number): Promise<{ job_id: string | null }> {
  return request<{ job_id: string | null }>(`/api/tasks/recalculate?object_id=${objectId}`, {
    method: "POST",
  });
}

// ── Справочники ──────────────────────────────────────────────────────────────

export interface Directory {
  id: number;
  code: string;
  name: string;
  icon: string | null;
  is_hierarchical: boolean;
  is_system: boolean;
  is_active: boolean;
  storage: string | null;
  settings: Record<string, unknown>;
}

export interface DirectoryRecord {
  id: number;
  directory_id: number;
  parent_id: number | null;
  name: string;
  values: Record<string, unknown>;
  is_archived: boolean;
  position: number;
}

export interface RecordPage {
  items: DirectoryRecord[];
  next_cursor: string | null;
  has_more: boolean;
}

export function listDirectories(includeInactive = false): Promise<Directory[]> {
  return request<Directory[]>(`/api/directories?include_inactive=${includeInactive}`);
}

export function createDirectory(draft: {
  code: string;
  name: string;
  icon?: string | null;
  is_hierarchical?: boolean;
}): Promise<Directory> {
  return request<Directory>("/api/directories", {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function updateDirectory(
  directoryId: number,
  patch: Partial<Pick<Directory, "name" | "icon" | "is_hierarchical" | "is_active" | "settings">>,
): Promise<Directory> {
  return request<Directory>(`/api/directories/${directoryId}`, { method: "PATCH", body: patch });
}

export function deleteDirectory(directoryId: number): Promise<undefined> {
  return request<undefined>(`/api/directories/${directoryId}`, { method: "DELETE" });
}

export function listRecords(
  directoryId: number,
  params: { q?: string; include_archived?: boolean; limit?: number; cursor?: string } = {},
): Promise<RecordPage> {
  const query = new URLSearchParams();
  if (params.q) query.set("q", params.q);
  if (params.include_archived) query.set("include_archived", "true");
  query.set("limit", String(params.limit ?? 100));
  if (params.cursor) query.set("cursor", params.cursor);
  return request<RecordPage>(`/api/directories/${directoryId}/records?${query.toString()}`);
}

export function createRecord(
  directoryId: number,
  draft: { name: string; parent_id?: number | null; values?: Record<string, unknown> },
): Promise<DirectoryRecord> {
  return request<DirectoryRecord>(`/api/directories/${directoryId}/records`, {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function updateRecord(
  recordId: number,
  patch: {
    name?: string;
    parent_id?: number | null;
    values?: Record<string, unknown>;
    is_archived?: boolean;
  },
): Promise<DirectoryRecord> {
  return request<DirectoryRecord>(`/api/directory-records/${recordId}`, {
    method: "PATCH",
    body: patch,
  });
}

export function deleteRecord(recordId: number): Promise<undefined> {
  return request<undefined>(`/api/directory-records/${recordId}`, { method: "DELETE" });
}

export interface ImportPreview {
  headers: string[];
  rows: Record<string, string>[];
  total: number;
}

export interface ImportSummary {
  created: number;
  updated: number;
  skipped: number;
}

export async function previewImport(directoryId: number, file: File): Promise<ImportPreview> {
  const form = new FormData();
  form.append("file", file);
  return uploadForm<ImportPreview>(`/api/directories/${directoryId}/import/preview`, form);
}

export async function runImport(
  directoryId: number,
  file: File,
  mapping: Record<string, string | number>,
  updateExisting: boolean,
): Promise<ImportSummary> {
  const form = new FormData();
  form.append("file", file);
  form.append("mapping", JSON.stringify(mapping));
  form.append("update_existing", String(updateExisting));
  return uploadForm<ImportSummary>(`/api/directories/${directoryId}/import`, form);
}

/** Загрузка файла идёт мимо `request`: тело — FormData, а не JSON. */
async function uploadForm<T>(path: string, form: FormData): Promise<T> {
  const { getAccessToken, ApiError } = await import("@/shared/api/client");
  const response = await fetch(path, {
    method: "POST",
    body: form,
    headers: getAccessToken() ? { Authorization: `Bearer ${getAccessToken() ?? ""}` } : {},
  });
  if (!response.ok) {
    const body = (await response.json()) as {
      error: { code: string; message: string; details?: Record<string, unknown> };
    };
    throw new ApiError(response.status, body);
  }
  return (await response.json()) as T;
}

// ── Шаблоны ──────────────────────────────────────────────────────────────────

export interface Template {
  id: number;
  kind: string;
  object_id: number | null;
  name: string;
  payload: Record<string, unknown>;
  rrule: string | null;
  schedule: string | null;
  is_active: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
}

export interface SchedulePreview {
  description: string;
  dates: string[];
}

export function listTemplates(kind?: string): Promise<Template[]> {
  const suffix = kind ? `?kind=${kind}` : "";
  return request<Template[]>(`/api/templates${suffix}`);
}

export function createTemplate(draft: {
  kind: string;
  name: string;
  payload: Record<string, unknown>;
  rrule?: string | null;
  object_id?: number | null;
}): Promise<Template> {
  return request<Template>("/api/templates", {
    method: "POST",
    body: draft,
    idempotencyKey: newIdempotencyKey(),
  });
}

export function updateTemplate(
  templateId: number,
  patch: {
    name?: string;
    payload?: Record<string, unknown>;
    rrule?: string | null;
    is_active?: boolean;
  },
): Promise<Template> {
  return request<Template>(`/api/templates/${templateId}`, { method: "PATCH", body: patch });
}

export function deleteTemplate(templateId: number): Promise<undefined> {
  return request<undefined>(`/api/templates/${templateId}`, { method: "DELETE" });
}

export function previewSchedule(rrule: string, limit = 5): Promise<SchedulePreview> {
  return request<SchedulePreview>("/api/templates/schedule/preview", {
    method: "POST",
    body: { rrule, limit },
  });
}

export function applyTemplate(
  templateId: number,
  body: { project_id?: number | null; values?: Record<string, unknown> } = {},
): Promise<{ task_ids: number[]; project_id: number | null; root_id: number | null }> {
  return request(`/api/templates/${templateId}/apply`, {
    method: "POST",
    body,
    idempotencyKey: newIdempotencyKey(),
  });
}
